import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ShoppingBag, MapPin, User, Lock, AlertCircle, CreditCard, CheckCircle, Copy, Check } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const PAYMENT_METHODS = [
  { id: "stripe", label: "Card / Bank", desc: "Instant • Auto-processed", color: "#635BFF", bg: "#f0efff", textColor: "white", icon: <CreditCard className="w-5 h-5" /> },
  { id: "zelle", label: "Zelle", desc: "Manual • Admin verifies", color: "#6D1ED4", bg: "#f5eeff", textColor: "white", icon: <span className="font-black text-lg">Z</span> },
  { id: "venmo", label: "Venmo", desc: "Manual • Admin verifies", color: "#3D95CE", bg: "#e8f4fd", textColor: "white", icon: <span className="font-black text-lg">V</span> },
  { id: "cashapp", label: "Cash App", desc: "Manual • Admin verifies", color: "#00C244", bg: "#e8faf0", textColor: "white", icon: <span className="font-black text-lg">$</span> },
  { id: "bitcoin", label: "Bitcoin", desc: "Manual • Admin verifies", color: "#F7931A", bg: "#fff5e6", textColor: "white", icon: <span className="font-black text-lg">₿</span> },
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={handleCopy} className="ml-2 p-1 rounded hover:bg-black/10 transition-colors" title="Copy">
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

function ManualOrderConfirmation({ order, paymentMethod, paymentInfo, onBack }) {
  const method = PAYMENT_METHODS.find(m => m.id === paymentMethod);
  const info = paymentInfo[paymentMethod]?.info || "";

  const getInstructions = () => {
    switch (paymentMethod) {
      case "zelle": return { where: info || "Contact admin for Zelle details", how: "Via Zelle app", note: "Include your order number in the memo" };
      case "venmo": return { where: info || "Contact admin for Venmo details", how: "Via Venmo app", note: "Include your order number in the note" };
      case "cashapp": return { where: info || "Contact admin for Cash App details", how: "Via Cash App", note: "Include your order number in the note" };
      case "bitcoin": return { where: info || "Contact admin for Bitcoin wallet", how: "Bitcoin wallet", note: "Email us your transaction hash/ID after sending" };
      default: return { where: "", how: "", note: "" };
    }
  };

  const instructions = getInstructions();
  const hasDigital = order.has_digital;

  return (
    <div className="min-h-screen py-16" style={{ backgroundColor: "#FDFBF7" }} data-testid="manual-order-confirmation">
      <div className="max-w-2xl mx-auto px-4">
        {/* Status */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: `${method?.color}20` }}>
            <CheckCircle className="w-10 h-10" style={{ color: method?.color }} />
          </div>
          <h1 className="text-4xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
            Order Submitted!
          </h1>
          <p className="text-[#5A5A5A] text-lg">Complete your payment to confirm the order.</p>
        </div>

        {/* Order Number */}
        <div className="bg-white rounded-3xl border border-[#E6E2D3] p-6 mb-4 text-center">
          <p className="text-sm text-[#5A5A5A] mb-1">Your Order Number</p>
          <div className="flex items-center justify-center gap-2">
            <p className="text-2xl font-bold font-mono text-[#2F5233]" data-testid="manual-order-number">{order.order_number}</p>
            <CopyButton text={order.order_number} />
          </div>
          <p className="text-xs text-[#5A5A5A] mt-2">Include this in your payment memo/note</p>
        </div>

        {/* Payment Instructions */}
        <div className="rounded-3xl p-6 mb-4 text-white" style={{ backgroundColor: method?.color }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              {method?.icon}
            </div>
            <h2 className="text-xl font-bold">Pay via {method?.label}</h2>
          </div>

          <div className="space-y-3">
            <div className="bg-white/15 rounded-2xl p-4">
              <p className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-1">Amount to Send</p>
              <p className="text-3xl font-bold">${order.total.toFixed(2)}</p>
            </div>

            <div className="bg-white/15 rounded-2xl p-4">
              <p className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-1">Send to ({instructions.how})</p>
              <div className="flex items-center justify-between">
                <p className="font-bold text-lg break-all">{info || "Contact admin for payment details"}</p>
                {info && <CopyButton text={info} />}
              </div>
            </div>

            <div className="bg-white/15 rounded-2xl p-4">
              <p className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-1">Important: Payment Note/Memo</p>
              <div className="flex items-center gap-2">
                <p className="font-bold font-mono">{order.order_number}</p>
                <CopyButton text={order.order_number} />
              </div>
              <p className="text-xs text-white/70 mt-1">{instructions.note}</p>
            </div>
          </div>
        </div>

        {/* Digital book notice */}
        {hasDigital && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4">
            <p className="font-semibold text-amber-800 text-sm">Digital Book Access</p>
            <p className="text-amber-700 text-sm mt-1">
              Your digital book will be unlocked in your library once we verify your payment. This usually takes 1-24 hours on business days.
            </p>
          </div>
        )}

        {/* What happens next */}
        <div className="bg-white rounded-3xl border border-[#E6E2D3] p-6 mb-6">
          <h3 className="font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>What Happens Next</h3>
          <div className="space-y-3">
            {[
              { step: "1", text: `Send $${order.total.toFixed(2)} via ${method?.label} with your order number in the memo` },
              { step: "2", text: "Our team verifies your payment (typically within 1-24 hours)" },
              { step: "3", text: hasDigital ? "Digital access unlocked immediately + physical books shipped" : "Your order is processed and books are shipped" },
              { step: "4", text: "You receive an email confirmation with tracking info" },
            ].map(({ step, text }) => (
              <div key={step} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-[#2F5233] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                  {step}
                </div>
                <p className="text-sm text-[#5A5A5A]">{text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button onClick={onBack} className="flex-1 border-2 border-[#2F5233] text-[#2F5233] py-3 rounded-full font-bold hover:bg-[#2F5233]/5 transition-all">
            Back to Shop
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CheckoutPage() {
  const { cartItems, cartSubtotal, cartShipping, cartTotal, hasPhysical, clearCart } = useCart();
  const { user, getToken } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: "",
    street: "", city: "", state: "", zip: "", country: "US",
  });
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [paymentInfo, setPaymentInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [manualOrder, setManualOrder] = useState(null);

  useEffect(() => {
    axios.get(`${API}/payment-methods`).then(res => setPaymentInfo(res.data)).catch(() => {});
  }, []);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const needsShipping = hasPhysical && !cartItems.every(i => i.product_id === "single_book_paid");
  const hasDigital = cartItems.some(i => i.product_id === "digital_book");

  const buildPayload = () => ({
    customer_name: form.name,
    customer_email: form.email,
    customer_phone: form.phone || null,
    shipping_address: needsShipping ? {
      street: form.street, city: form.city,
      state: form.state, zip_code: form.zip, country: form.country
    } : null,
    items: cartItems.map(i => ({
      product_id: i.product_id, name: i.name,
      quantity: 1, price: i.price, shipping: i.shipping, type: i.type
    })),
    user_id: user?.id || null,
    payment_method: paymentMethod,
  });

  const validate = () => {
    if (!form.name || !form.email) return "Name and email are required.";
    if (needsShipping && (!form.street || !form.city || !form.state || !form.zip)) return "Please complete the shipping address.";
    if (hasDigital && !user) return "Please log in to purchase the digital book.";
    return null;
  };

  const handleStripeSubmit = async () => {
    const headers = { "Content-Type": "application/json" };
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await axios.post(`${API}/checkout/session`, buildPayload(), { headers });
    clearCart();
    window.location.href = res.data.checkout_url;
  };

  const handleManualSubmit = async () => {
    const headers = { "Content-Type": "application/json" };
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
    const res = await axios.post(`${API}/checkout/manual`, buildPayload(), { headers });
    clearCart();
    setManualOrder(res.data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    setError("");
    setLoading(true);
    try {
      if (paymentMethod === "stripe") {
        await handleStripeSubmit();
      } else {
        await handleManualSubmit();
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (manualOrder) {
    return (
      <ManualOrderConfirmation
        order={manualOrder}
        paymentMethod={paymentMethod}
        paymentInfo={paymentInfo}
        onBack={() => navigate("/shop")}
      />
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#FDFBF7" }}>
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 text-[#E6E2D3] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-[#1A1A1A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Your cart is empty</h2>
          <button onClick={() => navigate("/shop")} className="mt-4 bg-[#2F5233] text-white px-8 py-3 rounded-full font-bold hover:bg-[#1E3A21] transition-all">
            Browse Books
          </button>
        </div>
      </div>
    );
  }

  const selectedMethod = PAYMENT_METHODS.find(m => m.id === paymentMethod);
  const isManual = paymentMethod !== "stripe";
  const manualInfo = paymentInfo[paymentMethod]?.info || "";

  return (
    <div className="min-h-screen py-12" style={{ backgroundColor: "#FDFBF7" }} data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-[#1A1A1A] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Checkout</h1>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact */}
              <div className="bg-white rounded-3xl p-6 border border-[#E6E2D3]">
                <h2 className="text-xl font-bold text-[#1A1A1A] mb-4 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  <User className="w-5 h-5 text-[#2F5233]" /> Contact Information
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Full Name *</label>
                    <input name="name" value={form.name} onChange={handleChange} required placeholder="Your full name"
                      className="w-full bg-[#F5F5F0] border border-transparent rounded-xl px-4 py-3 focus:outline-none focus:border-[#2F5233] transition-colors"
                      data-testid="checkout-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Email Address *</label>
                    <input name="email" value={form.email} onChange={handleChange} required type="email" placeholder="your@email.com"
                      className="w-full bg-[#F5F5F0] border border-transparent rounded-xl px-4 py-3 focus:outline-none focus:border-[#2F5233] transition-colors"
                      data-testid="checkout-email" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Phone (Optional)</label>
                    <input name="phone" value={form.phone} onChange={handleChange} type="tel" placeholder="(555) 000-0000"
                      className="w-full bg-[#F5F5F0] border border-transparent rounded-xl px-4 py-3 focus:outline-none focus:border-[#2F5233] transition-colors"
                      data-testid="checkout-phone" />
                  </div>
                </div>
              </div>

              {/* Shipping */}
              {needsShipping && (
                <div className="bg-white rounded-3xl p-6 border border-[#E6E2D3]">
                  <h2 className="text-xl font-bold text-[#1A1A1A] mb-4 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    <MapPin className="w-5 h-5 text-[#2F5233]" /> Shipping Address
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Street Address *</label>
                      <input name="street" value={form.street} onChange={handleChange} required placeholder="123 Main Street"
                        className="w-full bg-[#F5F5F0] border border-transparent rounded-xl px-4 py-3 focus:outline-none focus:border-[#2F5233] transition-colors"
                        data-testid="checkout-street" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">City *</label>
                      <input name="city" value={form.city} onChange={handleChange} required placeholder="City"
                        className="w-full bg-[#F5F5F0] border border-transparent rounded-xl px-4 py-3 focus:outline-none focus:border-[#2F5233] transition-colors"
                        data-testid="checkout-city" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">State *</label>
                      <input name="state" value={form.state} onChange={handleChange} required placeholder="State"
                        className="w-full bg-[#F5F5F0] border border-transparent rounded-xl px-4 py-3 focus:outline-none focus:border-[#2F5233] transition-colors"
                        data-testid="checkout-state" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">ZIP Code *</label>
                      <input name="zip" value={form.zip} onChange={handleChange} required placeholder="12345"
                        className="w-full bg-[#F5F5F0] border border-transparent rounded-xl px-4 py-3 focus:outline-none focus:border-[#2F5233] transition-colors"
                        data-testid="checkout-zip" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">Country</label>
                      <select name="country" value={form.country} onChange={handleChange}
                        className="w-full bg-[#F5F5F0] border border-transparent rounded-xl px-4 py-3 focus:outline-none focus:border-[#2F5233] transition-colors"
                        data-testid="checkout-country">
                        <option value="US">United States</option>
                        <option value="CA">Canada</option>
                        <option value="GB">United Kingdom</option>
                        <option value="AU">Australia</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Method Selection */}
              <div className="bg-white rounded-3xl p-6 border border-[#E6E2D3]">
                <h2 className="text-xl font-bold text-[#1A1A1A] mb-4 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  <Lock className="w-5 h-5 text-[#2F5233]" /> Payment Method
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="payment-method-selector">
                  {PAYMENT_METHODS.map(method => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => setPaymentMethod(method.id)}
                      className={`relative p-4 rounded-2xl border-2 transition-all text-left ${
                        paymentMethod === method.id
                          ? "border-[#2F5233] shadow-md"
                          : "border-[#E6E2D3] hover:border-[#B2D3C2]"
                      }`}
                      style={paymentMethod === method.id ? { borderColor: method.color } : {}}
                      data-testid={`payment-method-${method.id}`}
                    >
                      {paymentMethod === method.id && (
                        <div className="absolute top-2 right-2 w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: method.color }}>
                          <Check className="w-2.5 h-2.5 text-white" />
                        </div>
                      )}
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-2" style={{ backgroundColor: method.color }}>
                        <span className="text-white">{method.icon}</span>
                      </div>
                      <p className="font-bold text-sm text-[#1A1A1A]">{method.label}</p>
                      <p className="text-xs text-[#5A5A5A] mt-0.5">{method.desc}</p>
                    </button>
                  ))}
                </div>

                {/* Manual payment instructions preview */}
                {isManual && (
                  <div className="mt-4 rounded-2xl p-4 text-white" style={{ backgroundColor: selectedMethod?.color }} data-testid="manual-payment-info">
                    <p className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-1">After placing order, send ${cartTotal.toFixed(2)} to:</p>
                    <p className="font-bold text-lg">
                      {manualInfo || <span className="text-white/60 italic">Configure in admin settings</span>}
                    </p>
                    <p className="text-xs text-white/70 mt-1">Include your order number in the payment memo/note</p>
                    {hasDigital && (
                      <p className="text-xs mt-2 bg-white/20 rounded-xl p-2">
                        Digital book will be unlocked after admin verifies your payment
                      </p>
                    )}
                  </div>
                )}
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-4 text-red-600" data-testid="checkout-error">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-full font-bold text-lg text-white transition-all hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{ backgroundColor: selectedMethod?.color || "#2F5233" }}
                data-testid="submit-order-btn"
              >
                {isManual ? null : <Lock className="w-5 h-5" />}
                {loading
                  ? "Processing..."
                  : isManual
                    ? `Submit Order — Pay $${cartTotal.toFixed(2)} via ${selectedMethod?.label}`
                    : `Pay $${cartTotal.toFixed(2)} with Stripe`
                }
              </button>
              {!isManual && <p className="text-center text-xs text-[#5A5A5A]">Secured by Stripe. Your payment information is encrypted.</p>}
              {isManual && <p className="text-center text-xs text-[#5A5A5A]">Order held pending manual payment verification. Digital access granted after confirmation.</p>}
            </form>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-6 border border-[#E6E2D3] sticky top-24">
              <h2 className="text-xl font-bold text-[#1A1A1A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Order Summary</h2>
              <div className="space-y-3 mb-4">
                {cartItems.map(item => (
                  <div key={item.product_id} className="flex justify-between items-start gap-2">
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-[#1A1A1A] leading-tight">{item.name}</p>
                      {item.shipping > 0
                        ? <p className="text-xs text-[#5A5A5A]">+${item.shipping.toFixed(2)} shipping</p>
                        : <p className="text-xs text-[#2F5233]">Free shipping</p>
                      }
                    </div>
                    <span className="font-bold text-[#2F5233] flex-shrink-0">${item.total.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#E6E2D3] pt-4 space-y-2">
                <div className="flex justify-between text-sm text-[#5A5A5A]">
                  <span>Subtotal</span><span>${cartSubtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-[#5A5A5A]">
                  <span>Shipping</span><span>{cartShipping > 0 ? `$${cartShipping.toFixed(2)}` : "Free"}</span>
                </div>
                <div className="flex justify-between font-bold text-[#1A1A1A] text-lg pt-2 border-t border-[#E6E2D3]">
                  <span>Total</span><span data-testid="order-total">${cartTotal.toFixed(2)}</span>
                </div>
              </div>

              {/* Selected payment badge */}
              {selectedMethod && (
                <div className="mt-4 flex items-center gap-2 p-3 rounded-xl" style={{ backgroundColor: `${selectedMethod.color}15` }}>
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs" style={{ backgroundColor: selectedMethod.color }}>
                    {selectedMethod.icon}
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#1A1A1A]">Paying via {selectedMethod.label}</p>
                    <p className="text-xs text-[#5A5A5A]">{selectedMethod.desc}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
